/**
 * ClockAccessOverrides
 * =============================================================================
 * Libera a tela de clock do tablet para uma funcionária, num resort, numa data
 * — sem mexer na escala.
 *
 * Usar quando alguém precisa bater ponto fora da escala (cobertura de última
 * hora, troca, evento). Não altera escala, meta nem pagamento: é só permissão
 * de aparecer na tela de clock daquele dia.
 *
 * Backend: clock_access.py  |  Tabela: sql/clock_access_overrides.sql
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, MenuItem, TextField, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Snackbar, Alert, CircularProgress, Chip, Divider,
} from '@mui/material';
import { Trash2, CalendarClock, Plus } from 'lucide-react';
import api from '../services/api';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function ClockAccessOverrides() {
  const [employees, setEmployees] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    employee_id: '', hotel_id: '', access_date: todayStr(), reason: '',
  });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const loadRows = useCallback(async () => {
    try {
      const res = await api.get('/clock-access-overrides');
      setRows(res.data || []);
    } catch {
      notify('Error loading clock releases', 'error');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [empRes, hotelRes] = await Promise.all([
          api.get('/api/employees_simple_list'),
          api.get('/hotels'),
        ]);
        // employees_simple_list devolve [id, name] ou objetos, conforme a rota
        const emps = (empRes.data || []).map(e =>
          Array.isArray(e) ? { id: e[0], name: e[1] } : { id: e.id, name: e.name }
        );
        setEmployees(emps.filter(e => e.id && e.name));
        setHotels(hotelRes.data || []);
        await loadRows();
      } catch {
        notify('Error loading employees/resorts', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadRows]);

  const canSubmit = form.employee_id && form.hotel_id && form.access_date && !saving;

  const handleAdd = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post('/clock-access-overrides', form);
      notify('Clock released');
      setForm(f => ({ ...f, employee_id: '', reason: '' }));
      await loadRows();
    } catch (e) {
      notify(e?.response?.data?.msg || 'Error releasing clock', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const previous = rows;
    setRows(rows.filter(r => r.id !== id));   // otimista
    try {
      await api.delete(`/clock-access-overrides/${id}`);
      notify('Release removed');
    } catch {
      setRows(previous);
      notify('Error removing release', 'error');
    }
  };

  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [employees]
  );

  return (
    <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CalendarClock size={20} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Clock Access</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Let an employee clock in/out at a resort on a specific date even if they are not on the
        schedule. Does not change the schedule, goals or payroll.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <TextField
              select label="Employee" size="small" sx={{ minWidth: 200 }}
              value={form.employee_id}
              onChange={(e) => setForm(f => ({ ...f, employee_id: e.target.value }))}
            >
              {sortedEmployees.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select label="Resort" size="small" sx={{ minWidth: 180 }}
              value={form.hotel_id}
              onChange={(e) => setForm(f => ({ ...f, hotel_id: e.target.value }))}
            >
              {hotels.map(h => (
                <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Date" type="date" size="small" sx={{ minWidth: 160 }}
              InputLabelProps={{ shrink: true }}
              value={form.access_date}
              onChange={(e) => setForm(f => ({ ...f, access_date: e.target.value }))}
            />

            <TextField
              label="Reason (optional)" size="small" sx={{ minWidth: 200, flexGrow: 1 }}
              value={form.reason}
              onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
            />

            <Button
              variant="contained" startIcon={<Plus size={16} />}
              onClick={handleAdd} disabled={!canSubmit}
            >
              Release
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Resort</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell align="center"><strong>Remove</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                      No upcoming releases
                    </TableCell>
                  </TableRow>
                ) : rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.access_date}
                        color={r.access_date === todayStr() ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{r.employee_name}</TableCell>
                    <TableCell>{r.hotel_name}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{r.reason || '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
